import React, { useState, useEffect } from 'react';
import Navbar from '../components/navbar/navbar';
import Footer from '../components/footer';
import api from '../utils/api';

const defaultSections = [
    { id: 'acceptance', icon: 'fa-handshake', title: 'Acceptance of Terms', content: [
        'By accessing and using the My Property Guru website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions.',
        'If you do not agree with any part of these terms, you must not use our platform.',
        'We reserve the right to update these terms at any time. Continued use of the platform after changes constitutes acceptance of the revised terms.',
    ]},
    { id: 'services', icon: 'fa-concierge-bell', title: 'Our Services', content: [
        'My Property Guru is an online real estate marketplace that connects property buyers with builders and developers.',
        'We provide property listings, project information, tour scheduling, and related services to facilitate real estate transactions.',
        'We act as an intermediary platform and do not directly sell, rent, or manage any properties listed on our website.',
        'Property details, pricing, and availability shown on our platform are provided by the respective builders and developers. While we strive for accuracy, we cannot guarantee all information is up-to-date.',
    ]},
    { id: 'accounts', icon: 'fa-user-gear', title: 'User Accounts', content: [
        'You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials.',
        'You must be at least 18 years of age to create an account and use our services.',
        'You are responsible for all activities that occur under your account and agree to notify us immediately of any unauthorized access.',
        'We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activity.',
    ]},
    { id: 'listings', icon: 'fa-list-check', title: 'Property Listings', content: [
        'Builders and developers are responsible for the accuracy of their project listings, including descriptions, images, pricing, and legal compliance information.',
        'My Property Guru does not verify the legal status of properties or the credentials of builders beyond basic information checks.',
        'Users should independently verify all property information and conduct due diligence before making any purchase decisions.',
        'We reserve the right to remove listings that violate our guidelines, contain misleading information, or fail to comply with RERA regulations.',
    ]},
    { id: 'conduct', icon: 'fa-scale-balanced', title: 'User Conduct', content: [
        'You agree not to use the platform for any illegal, fraudulent, or harmful purpose.',
        'You shall not post false or misleading information, spam, or objectionable content.',
        'Scraping, crawling, or automated data collection from our platform without written consent is prohibited.',
        'Impersonating another person or entity, or misrepresenting your affiliation with any party is strictly prohibited.',
    ]},
    { id: 'ip', icon: 'fa-copyright', title: 'Intellectual Property', content: [
        'All content on My Property Guru, including text, images, logos, design, and software, is our property or licensed to us and protected by copyright laws.',
        'You may not reproduce, distribute, or create derivative works from our content without prior written permission.',
        'Builders retain ownership of their project images and descriptions shared on the platform.',
    ]},
    { id: 'liability', icon: 'fa-triangle-exclamation', title: 'Limitation of Liability', content: [
        'My Property Guru is not liable for any direct, indirect, incidental, or consequential damages arising from use of our platform.',
        'We do not guarantee the quality, safety, or legality of any property listed on our platform.',
        'Any transaction between buyers and builders is solely between those parties. We are not responsible for disputes arising from such transactions.',
        'Our total liability to you for any claim shall not exceed the amount paid by you, if any, for using our services.',
    ]},
    { id: 'disputes', icon: 'fa-gavel', title: 'Dispute Resolution', content: [
        'Any disputes arising from these Terms & Conditions shall be governed by the laws of India.',
        'All disputes shall be subject to the exclusive jurisdiction of the courts in Ahmedabad, Gujarat, India.',
        'Before initiating legal proceedings, parties agree to attempt resolution through mediation or direct negotiation.',
    ]},
    { id: 'contact', icon: 'fa-phone', title: 'Contact Information', content: [
        'For questions regarding these Terms & Conditions, please contact us:',
        'Email: info@mypropertyguru.in | Phone: +91 95370 02666',
        'Address: Ahmedabad, Gujarat, India',
    ]},
];

export default function TermsAndConditions() {
    const [content, setContent] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await api.getSystemSettings();
                if (data && data.termsConditions) {
                    setContent(data.termsConditions);
                }
            } catch (err) {
                console.error('Failed to fetch terms and conditions:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    return (
        <>
            <Navbar transparent={false} />
            <section style={{
                background: 'linear-gradient(135deg, #05144b 0%, #0a2a8d 50%, #1e40af 100%)',
                padding: '80px 0 60px', position: 'relative', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', top: '20%', right: '15%', width: '80px', height: '80px', borderRadius: '16px', background: 'rgba(241,174,36,0.1)', transform: 'rotate(45deg)' }}></div>
                <div className="container position-relative" style={{ zIndex: 2 }}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <div style={{ width: '40px', height: '3px', background: '#f1ae24', borderRadius: '2px' }}></div>
                        <span style={{ color: '#f1ae24', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px' }}>Legal</span>
                    </div>
                    <h1 className="text-white fw-bold mb-3" style={{ fontSize: '2.5rem' }}>Terms & <span style={{ color: '#f1ae24' }}>Conditions</span></h1>
                    <p className="mb-0" style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '600px', lineHeight: 1.7 }}>
                        Please read these terms carefully before using our platform and services.
                    </p>
                </div>
            </section>

            <section style={{ background: '#f8f9fa', padding: '60px 0 80px', minHeight: '400px' }}>
                <div className="container">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : content ? (
                        <div className="bg-white rounded-4 shadow-sm p-5" style={{ border: '1px solid #eee' }}>
                            <div className="legal-content-dynamic" dangerouslySetInnerHTML={{ __html: content }} />
                            <style>{`
                                .legal-content-dynamic h1, .legal-content-dynamic h2, .legal-content-dynamic h3 {
                                    font-weight: 700;
                                    color: #05144b;
                                    margin-top: 1.5rem;
                                    margin-bottom: 1rem;
                                }
                                .legal-content-dynamic p {
                                    color: #555;
                                    line-height: 1.8;
                                    margin-bottom: 1rem;
                                }
                                .legal-content-dynamic ul, .legal-content-dynamic ol {
                                    margin-bottom: 1.5rem;
                                }
                                .legal-content-dynamic li {
                                    color: #555;
                                    line-height: 1.8;
                                    margin-bottom: 0.5rem;
                                }
                            `}</style>
                        </div>
                    ) : (
                        <div className="row g-4">
                            <div className="col-lg-3">
                                <div className="bg-white rounded-4 shadow-sm p-3 sticky-top" style={{ top: '100px', zIndex: 1 }}>
                                    <h6 className="fw-bold text-muted mb-3 px-2" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Sections</h6>
                                    {defaultSections.map((s, i) => (
                                        <a key={i} href={`#${s.id}`} className="d-flex align-items-center gap-2 rounded-3 mb-1 px-3 py-2 text-decoration-none"
                                            style={{ color: '#333', fontWeight: 500, fontSize: '0.82rem', transition: 'all 0.2s' }}>
                                            <span className="fw-bold" style={{ color: '#05144b', width: '18px', fontSize: '0.7rem' }}>{String(i + 1).padStart(2, '0')}</span>
                                            {s.title}
                                        </a>
                                    ))}
                                </div>
                            </div>

                            <div className="col-lg-9">
                                {defaultSections.map((s, i) => (
                                    <div key={i} id={s.id} className="bg-white rounded-4 shadow-sm p-4 mb-4" style={{ border: '1px solid #eee' }}>
                                        <div className="d-flex align-items-center gap-3 mb-3">
                                            <div style={{
                                                width: '42px', height: '42px', borderRadius: '10px', background: '#05144b',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                            }}>
                                                <i className={`fa-solid ${s.icon} text-white`} style={{ fontSize: '14px' }}></i>
                                            </div>
                                            <div>
                                                <span style={{ color: '#f1ae24', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Section {String(i + 1).padStart(2, '0')}</span>
                                                <h5 className="fw-bold mb-0">{s.title}</h5>
                                            </div>
                                        </div>
                                        <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                            {s.content.map((item, j) => (
                                                <li key={j} style={{ color: '#555', lineHeight: 1.8, fontSize: '0.9rem', marginBottom: '10px', listStyleType: 'disc' }}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <Footer />
        </>
    );
}
